import { useMemo, useRef, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { WebP } from '/src/components/WebP';
import classNames from 'classnames';
import { useAtom } from 'jotai';

import { useSelection } from '/src/hooks';
import { formatCurrency } from '/src/utils';
import { LazyImg } from '/src/components/LazyImg';
import { goalDetailSelectedTriggerType } from '/src/features/goals/store';
import { RenderTriggerText, combinedTriggerAndDisplayData } from '/src/features/goals/utils';

import { ReactTable, Checkbox, Select, Tooltip, SkeletonLine } from '@crazyegginc/hatch';
import { DEVICE_TYPES, SORT_ORDER_TYPES } from '/src/features/_global/constants';
import {
  GOAL_TRIGGERS_METADATA,
  MAXIMUM_SELECTION,
  COLOR_MATRIX,
  TRIGGER_CHART_TYPES,
} from '/src/features/goals/constants';

function VariantColor({ index }) {
  const barClasses = 'absolute bottom-px top-0 w-[5px] pb-px left-0';
  if (index === -1) {
    return <div className={barClasses}>&nbsp;</div>;
  }

  const color = COLOR_MATRIX[index].tw ?? 'bg-[#a5acbc]';

  return <div className={classNames(barClasses, color)}>&nbsp;</div>;
}

function GoalTriggersTable({ triggers, goalId, hasValueSetAndAmount, isFetchingCounts }) {
  const tableRef = useRef(null);
  const [order, setOrder] = useState({
    countType: '',
    sort: SORT_ORDER_TYPES.DESC,
  });

  const sortedTriggers = useMemo(
    () =>
      [...triggers].sort((a, b) => {
        const aValue = a.counts[order.countType] ?? 0;
        const bValue = b.counts[order.countType] ?? 0;
        if (order.countType) {
          return order.sort === SORT_ORDER_TYPES.DESC
            ? parseInt(bValue) - parseInt(aValue)
            : parseInt(aValue) - parseInt(bValue);
        }
      }),
    [triggers, order.countType, order.sort],
  );

  const visibleTriggers = useMemo(() => {
    // only show deleted triggers if they have conversions:
    // https://app.shortcut.com/crazyegg/story/62175
    return sortedTriggers.filter((t) => {
      return !t.deletedAt || Object.keys(t.counts).length > 0;
    });
  }, [sortedTriggers]);

  const currency = triggers[0].counts?.totalValue?.displayCurrency ?? 'USD';
  const [type, setType] = useAtom(goalDetailSelectedTriggerType);
  const { currentSelection, setSelection, clearSelection, toggleSelection } = useSelection();

  const maxSelectable = useMemo(() => Math.min(MAXIMUM_SELECTION, visibleTriggers.length), [visibleTriggers]);

  const columns = useMemo(
    () => [
      {
        header: () => (
          <div>
            <Checkbox
              checked={currentSelection.length && currentSelection.length === maxSelectable ? true : false}
              onChange={() => {
                currentSelection.length === maxSelectable
                  ? clearSelection()
                  : setSelection([...visibleTriggers.slice(0, maxSelectable)]);
              }}
              indeterminate={currentSelection.length > 0 && currentSelection.length < maxSelectable}
              title="Toggle all rows selected"
              size="lg"
            />
          </div>
        ),
        cell: ({ row }) => {
          const isSelected = currentSelection.some((x) => x.id === row.original.id);
          const isMaxSelected = currentSelection.length >= MAXIMUM_SELECTION && !isSelected;

          return (
            <>
              <Tooltip
                show={isMaxSelected}
                interactive={true}
                darkTheme={true}
                placement="bottom"
                tooltipContent={`You can only select up to ${MAXIMUM_SELECTION} goal triggers.`}
              >
                <div>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleSelection(row.original)}
                    size="lg"
                    disabled={isMaxSelected}
                    checkBoxClass={classNames({ '!bg-mystic-500 !opacity-30': isMaxSelected })}
                  />
                </div>
              </Tooltip>
              <VariantColor index={isSelected ? row.original.position : -1} />
            </>
          );
        },
        enableSorting: false,
        id: 'selection',
        size: 60,
        meta: {
          align: 'center',
        },
      },
      {
        header: () => (
          <div className="mr-5 flex items-center">
            Goal triggers{' '}
            <div className=" ml-[30px]">
              <Select
                options={[
                  { label: 'Graph each trigger separately', value: TRIGGER_CHART_TYPES.SEPARATE },
                  { label: 'Combine selected triggers into one graph', value: TRIGGER_CHART_TYPES.COMBINE },
                ]}
                value={type}
                onChange={(value) => setType(value)}
                aria-label="triggers chart type"
                style={{ width: '340px' }}
                popperClassName="!normal-case"
              />
            </div>
          </div>
        ),
        id: 'goalTriggersName',
        enableSorting: false,
        size: 500,
        meta: {
          align: 'center',
          justify: 'left',
        },
        cell: function SnapshotVisitsCell({ row }) {
          return <SingleTrigger triggerAndSegments={row.original} isTable={true} />;
        },
      },
      {
        header: () => (
          <div className="text-right leading-[14px] w-full">
            Total
            <br />
            conversions
          </div>
        ),
        accessorKey: 'conversionsCount',
        meta: {
          justify: 'center',
          align: 'right',
        },
        cell: ({ row }) => {
          return <span className="text-right w-full">{row?.original?.counts?.conversionsCount ?? 0}</span>;
        },
      },
      {
        header: () => (
          <div className="text-right leading-[14px] w-full">
            Visitors
            <br />
            Converting
          </div>
        ),
        accessorKey: 'uniqueConversionsCount',
        meta: {
          align: 'center',
          justify: 'right',
        },
        cell: function MemberCell({ row }) {
          return <span className="text-right w-full">{row?.original?.counts?.uniqueConversionsCount ?? 0}</span>;
        },
      },
      {
        header: () => (
          <div className="text-right leading-[14px] w-full">
            Total goal
            <br />
            Value {hasValueSetAndAmount && `(${currency})`}
          </div>
        ),
        accessorKey: 'totalGoalValue',
        meta: {
          align: 'center',
          justify: 'center',
        },
        enableSorting: false,
        cell: function MemberCell({ row }) {
          const triggerHasValueSet = !!row.original.valueType;
          const triggerValue = row.original?.counts?.totalValue?.displayWorth ?? 0;

          if (triggerHasValueSet || triggerValue > 0) {
            return (
              <span className="text-right w-full">
                {formatCurrency(triggerValue, currency, { precision: 10 ** 4 })}
              </span>
            );
          }

          if (!!row.original.deletedAt && row.original.deletedAt > 0) {
            return <span className="text-right w-full">{formatCurrency(0, currency, { precision: 10 ** 4 })}</span>;
          }

          return (
            <Link to={`/goals/${goalId}/edit-value`} className="text-link text-right w-full">
              Set goal value
            </Link>
          );
        },
      },
    ],
    [
      type,
      setType,
      clearSelection,
      currentSelection,
      setSelection,
      toggleSelection,
      currency,
      goalId,
      hasValueSetAndAmount,
      maxSelectable,
      visibleTriggers,
    ],
  );

  const tableSortData = useMemo(() => {
    return {
      id: order.countType,
      desc: order.sort === SORT_ORDER_TYPES.DESC,
    };
  }, [order.countType, order.sort]);

  const onFetchData = useCallback(
    ({ sorting }) => {
      if (sorting) {
        setOrder({
          countType: sorting[0].id,
          sort: sorting[0].desc ? SORT_ORDER_TYPES.DESC : SORT_ORDER_TYPES.ASC,
        });
      }
    },
    [setOrder],
  );

  return (
    <ReactTable
      columns={columns}
      data={visibleTriggers || []}
      ref={{ tableRef }}
      onFetchData={onFetchData}
      sorting={tableSortData}
      rowHeight={70}
      fetching={isFetchingCounts}
      className="!border-none !rounded-none"
      rowCustomBackground={(row) =>
        classNames({
          'bg-white': row,
        })
      }
      rowCustomClassname={() =>
        classNames({
          'after:!border-solid': true,
          '!pr-10': true,
        })
      }
      headerCustomClassname="!pr-10"
    />
  );
}

function SingleTrigger({ triggerAndSegments, device = DEVICE_TYPES.DESKTOP, isTable = false }) {
  const trigger = isTable ? triggerAndSegments : triggerAndSegments[0];
  const isText = typeof trigger.segments[0] === 'string';
  const isDeleted = !!trigger.deletedAt && trigger.deletedAt > 0;

  return (
    <div className={classNames('flex w-full items-center', { 'h-[70px] pl-[26px]': !isTable })}>
      {isText ? (
        <div className="h-10 w-10 rounded bg-solitude-500 flex items-center justify-center">
          <WebP
            className="w-[25px]"
            webp={GOAL_TRIGGERS_METADATA[trigger.type.toUpperCase()]?.webp ?? GOAL_TRIGGERS_METADATA.OTHER.webp}
            fallback={
              GOAL_TRIGGERS_METADATA[
                GOAL_TRIGGERS_METADATA[trigger.type.toUpperCase()]?.png ?? GOAL_TRIGGERS_METADATA.OTHER.png
              ]
            }
            width="25"
            alt="trigger type"
          />
        </div>
      ) : (
        <Tooltip
          show={trigger?.segments[0]?.onHover?.type === 'editor'}
          interactive={true}
          darkTheme={false}
          placement="bottom"
          tooltipContent={
            <LazyImg
              className="h-[88px] w-[138px] !rounded-none"
              alt="goal thumbnail"
              src={trigger?.segments[0]?.onHover?.arguments?.[0]?.[`thumbnail_for_${device.toLowerCase()}`] ?? null}
              width="138px"
              height="88px"
            />
          }
        >
          <div className="h-10 w-10 rounded bg-solitude-500 flex items-center justify-center">
            <WebP
              className="w-[25px]"
              webp={GOAL_TRIGGERS_METADATA[trigger.type.toUpperCase()]?.webp ?? GOAL_TRIGGERS_METADATA.OTHER.webp}
              fallback={
                GOAL_TRIGGERS_METADATA[
                  GOAL_TRIGGERS_METADATA[trigger.type.toUpperCase()]?.png ?? GOAL_TRIGGERS_METADATA.OTHER.png
                ]
              }
              width="25"
              alt="trigger type"
            />
          </div>
        </Tooltip>
      )}

      <div className="flex flex-col text-left ml-5">
        <RenderTriggerText trigger={trigger} theme="light" single={true} isDeleted={isDeleted} />
      </div>
    </div>
  );
}

export function DetailCardTriggers({ goal, triggersCounts, hasValueSetAndAmount, isFetchingCounts }) {
  const triggerAndSegments = combinedTriggerAndDisplayData(goal, triggersCounts);

  const visibleTriggers = triggerAndSegments.filter((t) => {
    // only show deleted triggers with data
    return !t.deletedAt || Object.keys(t.counts).length > 0;
  });

  if (visibleTriggers.length <= 0) return null;

  if (visibleTriggers.length > 1) {
    return (
      <GoalTriggersTable
        triggers={triggerAndSegments}
        goalId={goal.id}
        hasValueSetAndAmount={hasValueSetAndAmount}
        isFetchingCounts={isFetchingCounts}
      />
    );
  }

  return isFetchingCounts ? (
    <div>
      <SkeletonLine className="!h-[37px] !rounded-sm" width="100%" />
    </div>
  ) : (
    <SingleTrigger triggerAndSegments={visibleTriggers} />
  );
}
